import { useState, useRef, forwardRef, Children } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { registerMenu, unregisterMenu } from '../../utils/dropdownRegistry';
import { IDropdown } from '../../types/Dropdown/dropdown';
import { cloneNestedComponent } from '../../utils/cloneNestedComponent';
import { Label } from '../Label';
import { DropdownButton } from './DropdownButton';
import { DropdownMenu } from './DropdownMenu';
import styles from './dropdown.module.scss';

export const Dropdown = forwardRef<HTMLDivElement, IDropdown>((
  {
    id,
    status = 'default',
    label,
    labelHint,
    hintText,
    children,
    width = 'full',
    closeOnClickOutside = true,
    focusOnMenuOpen = false,
    onToggleHandler
  },
  ref?
) => {

  const dropdownRef = useRef<HTMLElement | null>(null);
  const menuElementRef = useRef<HTMLElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    onToggleHandler?.(!isOpen);
  };
  const closeDropdown = () => {
    setIsOpen(false);
    onToggleHandler?.(false);
  };

  // Ensure we always keep an internal ref to the trigger element so code can
  // access it even when a parent passes a callback ref.
  const handleTriggerRef = (el: HTMLElement | null) => {
    dropdownRef.current = el;
    try {
      if (typeof ref === 'function') {
        ref(el as any);
      } else if (ref) {
        (ref as any).current = el;
      }
    } catch (e) {
      // ignore
    }
  };

  // Listen for clicks outside both the trigger wrapper and the portaled menu element
  closeOnClickOutside && useClickOutside([(ref || dropdownRef as any), menuElementRef], closeDropdown, isOpen);

  return (
    <div
      id={id}
      className={styles['dropdown']}
      {...(width === 'full' || width === 'fit-content' ? { 'data-dropdown-width': width } : { style: { width: width } })}
      data-dropdown-focused={focusOnMenuOpen}
    >
      {(label || labelHint) && <Label text={label} hintText={labelHint} />}

      <div className={styles['dropdown__children-wrapper']} ref={handleTriggerRef}>
        {
          Children.map(children, (child: any) => {

            const childDropdownMenu = cloneNestedComponent(child, DropdownMenu, {
              isOpen: isOpen,
              closeDropdown: closeDropdown,
              dropdownRef: ref || dropdownRef,
              // Provide a way for the menu to register its actual DOM node so
              // click-outside detection can account for portaled menus.
              registerMenuRef: (el: HTMLElement | null) => {
                // Unregister previous menu element if different
                if (menuElementRef.current && menuElementRef.current !== el) {
                  unregisterMenu(menuElementRef.current);
                }
                menuElementRef.current = el;
                if (el) {
                  const triggerEl = dropdownRef.current || null;
                  registerMenu(el, triggerEl as HTMLElement | null);
                }
              }
            });

            const childDropdownButton = cloneNestedComponent(child, DropdownButton, {
              toggleDropdown: toggleDropdown,
              closeDropdown: closeDropdown
            });

            if (childDropdownMenu) {
              return childDropdownMenu;
            } else if (childDropdownButton) {
              return childDropdownButton;
            } else {
              return child;
            }
          })
        }
      </div>

      {hintText && <div className={styles['dropdown__hint-text']} data-dropdown-status={status}>{hintText}</div>}
    </div>
  )
});